import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer,
  Tooltip as ChartTooltip, XAxis, YAxis,
} from "recharts";
import { RotateCcw, TrendingUp } from "lucide-react";
import {
  formatCount, formatMoney, formatMoneyShort, getForecast, metricsUrls, monthLabel,
  type ForecastResponse, type ForecastScenario, type Segment,
} from "@/lib/metrics";
import { EmptyState, ErrorState, TableSkeleton, Tile } from "./shared";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

interface Overrides {
  newMrrPerMonth: string;
  newCustomersPerMonth: string;
  revenueChurnRate: string;
  customerChurnRate: string;
}

const BLANK: Overrides = { newMrrPerMonth: "", newCustomersPerMonth: "", revenueChurnRate: "", customerChurnRate: "" };

function num(value: string): number | undefined {
  if (value.trim() === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

export default function ForecastTab({ segment }: { segment: Segment }) {
  const [scenario, setScenario] = useState<ForecastScenario>("base");
  const [draft, setDraft] = useState<Overrides>(BLANK);
  const [applied, setApplied] = useState<Overrides>(BLANK);

  const newMrr = num(applied.newMrrPerMonth);
  const revChurn = num(applied.revenueChurnRate);
  const custChurn = num(applied.customerChurnRate);

  const params = {
    ...segment,
    months: 12,
    scenario,
    // J$ in the box, cents on the wire.
    newMrrPerMonth: newMrr != null ? Math.round(newMrr * 100) : undefined,
    newCustomersPerMonth: num(applied.newCustomersPerMonth),
    // percent in the box, 0-1 on the wire.
    revenueChurnRate: revChurn != null ? revChurn / 100 : undefined,
    customerChurnRate: custChurn != null ? custChurn / 100 : undefined,
  };

  const { data, isLoading, error } = useQuery<ForecastResponse>({
    queryKey: [metricsUrls.forecast(params)],
    queryFn: () => getForecast(params),
  });

  const chartData = useMemo(() => {
    if (!data) return [];
    const history = data.history.map((h) => ({
      month: monthLabel(h.month),
      raw: h.month,
      mrr: h.mrr,
      forecastMrr: null as number | null,
      customers: h.customers,
    }));
    if (history.length > 0 && data.months.length > 0) {
      history[history.length - 1].forecastMrr = history[history.length - 1].mrr;
    }
    const future = data.months.map((m) => ({
      month: monthLabel(m.month),
      raw: m.month,
      mrr: null as number | null,
      forecastMrr: m.mrr,
      customers: m.customers,
    }));
    return [...history, ...future];
  }, [data]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(applied);
  const hasOverrides = JSON.stringify(applied) !== JSON.stringify(BLANK);

  if (isLoading) return <Card><CardContent className="pt-6"><TableSkeleton rows={8} cols={6} /></CardContent></Card>;
  if (error) return <Card><CardContent className="pt-6"><ErrorState error={error} /></CardContent></Card>;
  if (!data) return null;

  const a = data.assumptions;
  const last = data.months[data.months.length - 1];
  const seasonality = Object.entries(a.seasonality || {}).sort(([x], [y]) => x.localeCompare(y));
  const boundary = data.history.length > 0 ? monthLabel(data.history[data.history.length - 1].month) : undefined;

  return (
    <div className="space-y-6" data-testid="tab-forecast">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="MRR in 12 months" value={formatMoney(last?.mrr)} sub={last ? monthLabel(last.month) : ""} testId="tile-forecast-mrr" />
        <Tile label="Customers in 12 months" value={formatCount(last?.customers)} />
        <Tile label="Measured on" value={`${a.basedOnMonths} months`} sub="Of your own history" />
        <Tile label="Scenario" value={scenario === "base" ? "Base case" : scenario === "optimistic" ? "Optimistic" : "Pessimistic"} sub={hasOverrides ? "With your overrides" : "Measured assumptions"} />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4" />Twelve months ahead</CardTitle>
              <CardDescription>Solid is what happened, dashed is the projection. Customers are on the right axis.</CardDescription>
            </div>
            <Tabs value={scenario} onValueChange={(v) => setScenario(v as ForecastScenario)}>
              <TabsList className="h-9">
                <TabsTrigger value="pessimistic" data-testid="scenario-pessimistic">Pessimistic</TabsTrigger>
                <TabsTrigger value="base" data-testid="scenario-base">Base</TabsTrigger>
                <TabsTrigger value="optimistic" data-testid="scenario-optimistic">Optimistic</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <EmptyState title="Nothing to forecast yet" hint="There is not enough billing history to project forward. Run a full sync, then come back." />
          ) : (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} minTickGap={16} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} width={70} tickFormatter={(v: number) => formatMoneyShort(v)} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} width={50} allowDecimals={false} />
                  <ChartTooltip formatter={(v: any, n: any) => [v == null ? "–" : n === "Customers" ? formatCount(Number(v)) : formatMoney(Number(v)), n]} />
                  <Legend />
                  {boundary && <ReferenceLine yAxisId="left" x={boundary} stroke="hsl(var(--muted-foreground))" strokeDasharray="2 2" label={{ value: "today", fontSize: 10, position: "top" }} />}
                  <Line yAxisId="left" type="monotone" dataKey="mrr" name="MRR" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls={false} />
                  <Line yAxisId="left" type="monotone" dataKey="forecastMrr" name="Projected MRR" stroke="hsl(var(--primary))" strokeWidth={2} strokeDasharray="5 4" dot={false} isAnimationActive={false} connectNulls />
                  <Line yAxisId="right" type="monotone" dataKey="customers" name="Customers" stroke="#0ea5e9" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Assumptions</CardTitle>
          <CardDescription>
            Measured from your own last {a.basedOnMonths} months. Type over any of them to see what a different world looks like; leave a box blank to keep the measured value.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label className="text-xs">New MRR per month (J$)</Label>
              <Input
                value={draft.newMrrPerMonth}
                onChange={(e) => setDraft({ ...draft, newMrrPerMonth: e.target.value })}
                placeholder={String(Math.round(a.newMrrPerMonth / 100))}
                data-testid="input-new-mrr"
              />
              <p className="text-xs text-muted-foreground mt-1">Measured {formatMoney(a.newMrrPerMonth)}</p>
            </div>
            <div>
              <Label className="text-xs">New customers per month</Label>
              <Input
                value={draft.newCustomersPerMonth}
                onChange={(e) => setDraft({ ...draft, newCustomersPerMonth: e.target.value })}
                placeholder={a.newCustomersPerMonth.toFixed(1)}
                data-testid="input-new-customers"
              />
              <p className="text-xs text-muted-foreground mt-1">Measured {a.newCustomersPerMonth.toFixed(1)}</p>
            </div>
            <div>
              <Label className="text-xs">Revenue churn % / month</Label>
              <Input
                value={draft.revenueChurnRate}
                onChange={(e) => setDraft({ ...draft, revenueChurnRate: e.target.value })}
                placeholder={(a.revenueChurnRate * 100).toFixed(1)}
                data-testid="input-revenue-churn"
              />
              <p className="text-xs text-muted-foreground mt-1">Measured {(a.revenueChurnRate * 100).toFixed(1)}%</p>
            </div>
            <div>
              <Label className="text-xs">Customer churn % / month</Label>
              <Input
                value={draft.customerChurnRate}
                onChange={(e) => setDraft({ ...draft, customerChurnRate: e.target.value })}
                placeholder={(a.customerChurnRate * 100).toFixed(1)}
                data-testid="input-customer-churn"
              />
              <p className="text-xs text-muted-foreground mt-1">Measured {(a.customerChurnRate * 100).toFixed(1)}%</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => setApplied(draft)} disabled={!dirty} data-testid="button-apply-assumptions">Apply assumptions</Button>
            <Button size="sm" variant="ghost" onClick={() => { setDraft(BLANK); setApplied(BLANK); }} disabled={!hasOverrides && !dirty}>
              <RotateCcw className="w-4 h-4 mr-1" />Back to measured
            </Button>
            <span className="text-xs text-muted-foreground">
              Expansion {(a.expansionRate * 100).toFixed(1)}% · contraction {(a.contractionRate * 100).toFixed(1)}% per month, both kept at the measured rate.
            </span>
          </div>

          {seasonality.length > 0 && (
            <div className="border-t pt-3">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">Seasonality applied</div>
              <div className="flex flex-wrap gap-1.5">
                {seasonality.map(([mm, factor]) => (
                  <Badge
                    key={mm}
                    variant="outline"
                    className={`font-normal ${factor > 1.05 ? "border-emerald-500/50" : factor < 0.95 ? "border-destructive/50" : ""}`}
                  >
                    {MONTH_NAMES[Number(mm) - 1] || mm} ×{factor.toFixed(2)}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Month by month</CardTitle>
          <CardDescription>The projected ledger behind the chart.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">MRR</TableHead>
                  <TableHead className="text-right">Customers</TableHead>
                  <TableHead className="text-right">New MRR</TableHead>
                  <TableHead className="text-right">Expansion</TableHead>
                  <TableHead className="text-right">Contraction</TableHead>
                  <TableHead className="text-right">Churn</TableHead>
                  <TableHead className="text-right">New cust.</TableHead>
                  <TableHead className="text-right">Churned cust.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.months.map((m) => (
                  <TableRow key={m.month} data-testid={`forecast-row-${m.month}`}>
                    <TableCell className="whitespace-nowrap font-medium">{monthLabel(m.month)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{formatMoney(m.mrr)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCount(m.customers)}</TableCell>
                    <TableCell className="text-right tabular-nums text-blue-600 dark:text-blue-400">{formatMoney(m.newMrr)}</TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-600 dark:text-emerald-400">{formatMoney(m.expansionMrr)}</TableCell>
                    <TableCell className="text-right tabular-nums text-amber-600 dark:text-amber-400">−{formatMoney(Math.abs(m.contractionMrr))}</TableCell>
                    <TableCell className="text-right tabular-nums text-destructive">−{formatMoney(Math.abs(m.churnMrr))}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCount(m.newCustomers)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCount(m.churnedCustomers)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
